import { useEffect, useRef, useState } from 'react';
import axiosInstance from '../axiosConfig';

// SpeechRecognition.lang is a fixed hint, not automatic language detection.
// Record speech and let the transcription service detect the spoken language.
export default function useFormVoiceInput({ onTranscript, onError }) {
  const [phase, setPhase] = useState('idle');
  const active = useRef(null);
  const mounted = useRef(true);
  const callbacks = useRef({ onTranscript, onError });
  callbacks.current = { onTranscript, onError };
  const supported = Boolean(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
  function release(session) {
    clearTimeout(session.timer);
    session.stream?.getTracks().forEach(track => track.stop());
  }
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const session = active.current;
      if (session) {
        session.cancelled = true;
        session.controller?.abort();
        if (session.recorder?.state === 'recording') session.recorder.stop();
        release(session);
      }
    };
  }, []);
  function stop() {
    const session = active.current;
    if (session?.recorder?.state === 'recording') session.recorder.stop();
  }
  async function start() {
    if (!supported || active.current) return;
    const session = { chunks: [], bytes: 0, cancelled: false };
    active.current = session;
    setPhase('requesting');
    try {
      session.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mounted.current || session.cancelled) { release(session); return; }
      const mime = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(type => window.MediaRecorder.isTypeSupported(type));
      const recorder = new window.MediaRecorder(session.stream, mime ? { mimeType: mime } : undefined);
      session.recorder = recorder;
      recorder.ondataavailable = event => {
        if (event.data.size) { session.chunks.push(event.data); session.bytes += event.data.size; }
        if (session.bytes > 10 * 1024 * 1024) { session.tooLarge = true; stop(); }
      };
      recorder.onerror = () => {
        session.failed = true;
        if (mounted.current) callbacks.current.onError('Recording complete nahi hui. Dobara try karein ya type kijiye.');
        if (recorder.state === 'recording') recorder.stop();
        release(session); active.current = null; if (mounted.current) setPhase('idle');
      };
      recorder.onstop = async () => {
        release(session);
        if (session.cancelled || !mounted.current || session.failed) return;
        setPhase('transcribing');
        try {
          if (session.tooLarge) throw new Error('Voice recording 10 MB se chhoti honi chahiye.');
          const type = (recorder.mimeType || mime || 'audio/webm').split(';')[0];
          const audio = new Blob(session.chunks, { type });
          if (!audio.size) throw new Error('Awaaz record nahi hui. Dobara boliye.');
          const body = new FormData();
          body.append('audio', audio, `voice.${type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm'}`);
          session.controller = new AbortController();
          const response = await axiosInstance.post('/api/form-assistant/transcribe', body, { timeout: 90000, signal: session.controller.signal });
          if (mounted.current && !session.cancelled) {
            if (!response.data.text?.trim()) throw new Error('Awaaz samajh nahi aayi. Dobara boliye.');
            callbacks.current.onTranscript(response.data.text.trim());
          }
        } catch (error) {
          if (mounted.current && !session.cancelled) callbacks.current.onError(error.response?.data?.error || error.message || 'Voice samajh nahi aayi. Dobara boliye ya type kijiye.');
        } finally {
          session.chunks = [];
          if (active.current === session) active.current = null;
          if (mounted.current) setPhase('idle');
        }
      };
      recorder.start(1000);
      setPhase('recording');
      session.timer = setTimeout(stop, 60000);
    } catch (error) {
      release(session); active.current = null;
      if (mounted.current) {
        setPhase('idle');
        callbacks.current.onError(error.name === 'NotAllowedError'
          ? 'Microphone permission nahi mili. Browser permission allow karein ya type kijiye.'
          : 'Microphone start nahi hua. Dobara try karein ya type kijiye.');
      }
    }
  }
  return { supported, listening: phase === 'recording', busy: phase !== 'idle', phase, start, stop };
}
